import { useEffect, useId, useState } from 'react';
import { generatePath, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserCircleIcon } from '@phosphor-icons/react';
import { DirectoryItemPage } from '@/features/DirectoryItemPage';
import {
    DirectoryItemEventsTab,
    PersonOverviewTab,
} from '@/features/DirectoryItemPage/components';
import {
    DirectoryItemEventFeed,
    usePersonOccurrences,
} from '@/features/EventFeed';
import { usePersonIdentityActions } from '@/features/IdentityActions';
import { AppRoutes } from '@/constants/routes';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { EngineService } from '@/services/engine';
import { revalidateByCacheKeyPrefix } from '@/utils/swr';
import { isNotFoundError } from '@/utils/http';
import type { Person, PersonOccurrence } from '@/types/person';
import { toast } from '@/components';
import {
    useDirectoryItemOccurrenceSelection,
    useDocumentTitle,
    useFeedFocus,
} from '@/hooks';
import { useEventFeedStore } from '@/stores';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import useSWR from 'swr';
import type { TabItem } from '@/components/Tabs';

const PERSON_DETAIL_TABS = {
    EVENTS: 'events',
    OVERVIEW: 'overview',
} as const;

type PersonDetailTab =
    (typeof PERSON_DETAIL_TABS)[keyof typeof PERSON_DETAIL_TABS];

interface PersonPageViewProps {
    personId: string;
    returnUrl: string;
}

export function PersonPageView({ personId, returnUrl }: PersonPageViewProps) {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);
    const pageInstanceId = useId();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<PersonDetailTab>(
        PERSON_DETAIL_TABS.OVERVIEW,
    );
    const setFeedInteraction = useEventFeedStore(
        (state) => state.setFeedInteraction,
    );
    const {
        focusedItem: selectedOccurrence,
        setFocusedItem: setSelectedOccurrence,
    } = useFeedFocus<PersonOccurrence>({
        entityId: personId,
        enabled: activeTab === PERSON_DETAIL_TABS.EVENTS,
        getId: (occurrence) => occurrence.event.id,
        setInteraction: setFeedInteraction,
    });
    const {
        data: person,
        isLoading: personLoading,
        error: personError,
        mutate,
    } = useSWR<Person>(
        `person-${personId}`,
        () => EngineService.getPerson(personId),
        { revalidateOnFocus: false },
    );

    const { actions: identityActions, modals: identityActionModals } =
        usePersonIdentityActions({
            person,
            includeDelete: true,
            mutateDetail: mutate,
            onChanged: () => {},
            onDeleted: () => navigate(returnUrl, { replace: true }),
            onMerged: (targetPersonId: string) =>
                navigate(
                    generatePath(AppRoutes.PERSON, {
                        personId: targetPersonId,
                    }),
                ),
            onSourceStale: () => navigate(returnUrl, { replace: true }),
        });

    const {
        data: occurrences,
        isLoading: occurrencesLoading,
        isLoadingMore: occurrencesLoadingMore,
        hasMore: hasMoreOccurrences,
        loadMore: loadMoreOccurrences,
        mutate: mutateOccurrences,
    } = usePersonOccurrences({
        personId,
        pageSize: 20,
    });

    useDocumentTitle('person', person?.name);

    useEffect(() => {
        setActiveTab(PERSON_DETAIL_TABS.OVERVIEW);
    }, [personId]);

    const { handleOccurrenceSelect } = useDirectoryItemOccurrenceSelection({
        occurrences,
        selectedOccurrence,
        setSelectedOccurrence,
        isLoading: occurrencesLoading,
        showEvents: () => setActiveTab(PERSON_DETAIL_TABS.EVENTS),
        hasMore: hasMoreOccurrences,
        loadMore: loadMoreOccurrences,
    });

    useEffect(() => {
        if (personError && !isNotFoundError(personError)) {
            toast.error(t('persons.failedToLoad'));
        }
    }, [personError, t]);

    const tabs: TabItem[] = [
        {
            id: PERSON_DETAIL_TABS.OVERVIEW,
            label: t('personPage.tabs.overview'),
        },
        {
            id: PERSON_DETAIL_TABS.EVENTS,
            label: t('personPage.tabs.events'),
        },
    ];

    if ((personError && !personLoading) || (!person && !personLoading)) {
        return <Navigate to={returnUrl} replace />;
    }

    if (!person) {
        return null;
    }

    const selectedEventId = selectedOccurrence?.event.id ?? null;

    const tabContent =
        activeTab === PERSON_DETAIL_TABS.EVENTS ? (
            <DirectoryItemEventsTab
                occurrence={selectedOccurrence}
                isOccurrencesLoading={occurrencesLoading}
                hasOccurrences={(occurrences ?? []).length > 0}
            />
        ) : (
            <PersonOverviewTab person={person} />
        );

    return (
        <>
            <DirectoryItemPage
                headerIcon={
                    <UserCircleIcon
                        className="text-grey-300"
                        weight="regular"
                    />
                }
                headerImageUrl={person.imageUrl}
                title={person.name || t('persons.card.unknownPerson')}
                tags={person.tags}
                actions={identityActions}
                content={{
                    kind: 'tabs',
                    tabs,
                    activeTab,
                    onTabChange: (tabId) =>
                        setActiveTab(tabId as PersonDetailTab),
                    tabContent,
                }}
                eventFeed={
                    <DirectoryItemEventFeed
                        key={pageInstanceId}
                        occurrences={occurrences}
                        isLoading={occurrencesLoading}
                        title={t('personPage.events')}
                        viewAllUrl={`${AppRoutes.EVENTS}?personId=${personId}`}
                        navigationFrom="person-events"
                        feedContext={{ type: 'person', personId }}
                        playingEventId={
                            activeTab === PERSON_DETAIL_TABS.EVENTS
                                ? selectedEventId
                                : null
                        }
                        onOccurrenceSelect={handleOccurrenceSelect}
                        linkSource={{ kind: 'person', id: personId }}
                        onEntityLinked={() => {
                            void mutate();
                            void mutateOccurrences();
                            void revalidateByCacheKeyPrefix(
                                CACHE_KEYS.ALL_PERSONS,
                            );
                        }}
                        onEntityUnlinked={(eventId) => {
                            if (selectedOccurrence?.event.id === eventId) {
                                setSelectedOccurrence(null);
                            }
                            void mutateOccurrences(
                                (pages) =>
                                    pages?.map((page) => ({
                                        ...page,
                                        items: page.items.filter(
                                            (item) => item.event.id !== eventId,
                                        ),
                                    })),
                                { revalidate: false },
                            );
                            void mutate();
                            void revalidateByCacheKeyPrefix(
                                CACHE_KEYS.ALL_PERSONS,
                            );
                        }}
                        pagination={{
                            hasMore: hasMoreOccurrences,
                            isLoadingMore: occurrencesLoadingMore,
                            onLoadMore: loadMoreOccurrences,
                        }}
                    />
                }
            />
            {identityActionModals}
        </>
    );
}
